/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { HubConnectionBuilder } from '@microsoft/signalr';
import Grid from '@mui/material/Grid';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import axios from 'axios';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { sendRequestAndReceiveResponse } from '../common/common';
import { getUploadIdInstrumentConstantsEndPoint, uploadConstantsEndPoint } from '../common/constants';
import LinearProgressWithLabel from '../common/LinearProgressWithLabel';
import { loadFromLocalStorage } from '../common/localStorage';
import logo from '../images/blueLogo.svg';
import icAdd from '../images/ic-add.svg';
import { actionCreators } from '../store/UploadConstants';
import DragDropConstants from './DragDropConstants';
import './styles.css';
import UploadConstantsSteps from './UploadConstantsSteps';


interface Props {
    displayName: any
    selectedPdf: any
    uploadDialogOpen: boolean
    fileName: any
    clearAll: Function
    handleUploadDialogClick: Function
    handleRedirection: Function
    UpdateFileName: Function
    UpdateSelectedPdf: Function
    UpdateFileType: Function
    selectedProductModels: any
    fileType: any
}
interface State {
    step: number
    isFormSubmitted: boolean
    isUploading: boolean
    isSuccess: boolean
    isFailure: boolean
    responseBody: {
        statusCode: number,
        uploadedFiles: any,
        repeatedFiles: any,
        failedFiles: any,
    }
    repeated: any
    failed: any
    progress: number
    uploadId: number
    totalFiles: number
}
class UploadConstants extends React.Component<Props, State> {
    state = {
        step: 1,
        isFormSubmitted: false,
        isUploading: false,
        isSuccess: false,
        isFailure: false,
        responseBody: {
            statusCode: 0,
            uploadedFiles: [],
            repeatedFiles: [],
            failedFiles: [{
                fileName: '',
                errorMessage: '',
            }]
        },
        repeated: '',
        failed: '',
        progress: 0,
        uploadId: 0,
        totalFiles: 0,
    };
    connection: any;
    handleClickOpen = () => {
        this.props.handleUploadDialogClick(true);
    };

    handleClose = () => {
        this.props.clearAll();
        this.props.handleUploadDialogClick(false);
        this.setState({ step: 1 });
    };
    handleSteps = (steps: number) => {
        if (steps) {
            this.setState({ step: steps });
        }
    };

    handleSubmit = async () => {
        const uploadRequest = new FormData();
        this.setState({ totalFiles: this.props.selectedPdf.length });
        for (const element of this.props.selectedPdf) {
            uploadRequest.append('filename', `${element.name}`);
            uploadRequest.append('formfile', element);
        }
        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
        };
        this.setState({ isFormSubmitted: true, isUploading: true });

        const res = await sendRequestAndReceiveResponse(
            JSON.stringify({ TotalFilesToUpload: this.props.selectedPdf.length }),
            getUploadIdInstrumentConstantsEndPoint
        );
        uploadRequest.append('uploadId', res.uploadId);
        this.setState({ uploadId: res.uploadId });
        const response = await axios.request({
            method: 'post',
            url: uploadConstantsEndPoint,
            data: uploadRequest,
            headers: {
                'Authorization': encodedToken,
            },
        });
        this.setState({ isUploading: false });
        const responsetext = await response.data;
        this.setState({ responseBody: responsetext });
        this.setState({
            progress: 0
        });
        this.setState({
            totalFiles: 0
        });
        let repeat = '';
        responsetext.repeatedFiles.map((x: any) => {
            repeat = repeat.concat(x);
            repeat = repeat.concat(',');
        });
        let fail = '';
        responsetext.failedFiles.map((x: any) => {
            fail = fail.concat(x.fileName);
            fail = fail.concat('-');
            fail = fail.concat(x.errorMessage);
            fail = fail.concat(',');
        });
        this.setState({ repeated: repeat });
        this.setState({ failed: fail });
        if (response.status === 201 || response.status === 200) {
            if (this.state.responseBody.uploadedFiles.length !== 0 && this.state.responseBody.repeatedFiles.length === 0 && this.state.responseBody.failedFiles.length === 0) {
                this.setState({ isSuccess: true }, () => {
                    this.redirect();
                });
            }
            else {
                this.setState({ isSuccess: true }, () => {
                    setTimeout(() => {
                        this.setState({
                            isSuccess: false, isFormSubmitted: false, isFailure: false, step: 1, responseBody: {
                                statusCode: 0,
                                uploadedFiles: [],
                                repeatedFiles: [],
                                failedFiles: [{
                                    fileName: '',
                                    errorMessage: '',
                                }]
                            }
                        });
                    }, 5000);
                });
            }
        }
        else {
            this.setState({ isFailure: true }, () => {
                setTimeout(() => {
                    this.setState({
                        isFormSubmitted: false, isFailure: false, step: 1, responseBody: {
                            statusCode: 0,
                            uploadedFiles: [],
                            repeatedFiles: [],
                            failedFiles: [{
                                fileName: '',
                                errorMessage: '',
                            }]
                        }
                    });
                }, 5000);
            });
        }
    };

    redirect = () => {
        setTimeout(() => {
            this.props.clearAll();
            this.props.handleUploadDialogClick(false);
            this.setState({ step: 1 });
            this.setState({ isSuccess: false });
            this.setState({ isFormSubmitted: false });
            this.setState({
                responseBody: {
                    statusCode: 0,
                    uploadedFiles: [],
                    repeatedFiles: [],
                    failedFiles: [{
                        fileName: '',
                        errorMessage: '',
                    }]
                }
            });
            this.props.handleRedirection(event);
        }, 5000);
    };
    componentDidMount() {
        const authToken = loadFromLocalStorage('AuthToken');
        if (authToken != null && authToken !== undefined) {
            this.connection = new HubConnectionBuilder().withUrl(`${import.meta.env.VITE_NOTIFICATION_HUB_URL +
                (import.meta.env.VITE_NOTIFICATION_HUB_URL === '' ? '' : '/')}hubs/alert?AuthToken=${authToken}`).build();
        }
        if (this.connection != null && this.connection !== undefined) {
            this.connection.start()
                .then((result: any) => {
                    console.log('Connected!');

                    this.connection.on('ReceiveAlert', (message: any) => {
                        if (message.uploadId === this.state.uploadId) {
                            this.setState({ progress: message.progress });
                        }
                    });
                })
                .catch((e: any) => console.log('Connection failed: ', e));
        }
    }
    componentWillUnmount() {
        this.connection.stop().then((result: any) => {
            console.log('Disconnected!');
        });
    }
    render() {
        const { isFormSubmitted, isUploading, isSuccess, isFailure, responseBody, repeated, failed, progress } = this.state;
        const { uploadDialogOpen } = this.props;

        return (
            <div>
                {<Button id="upload-button" onClick={this.handleClickOpen}>
                    <Grid container justifyContent="space-evenly">
                        <img src={icAdd}
                            className="ic_add" />
                        <span className="uploadButtonText">Upload</span>
                    </Grid>
                </Button>
                }
                <Dialog
                    fullScreen
                    className="UploadDialog"
                    open={uploadDialogOpen}
                    onClose={this.handleClose}
                >
                    <DialogTitle className="dialogTitle">
                        <Grid container justifyContent="space-between">

                            <span>
                                <img src={logo}
                                />
                                <span className="boldFont uploadTitle">Instrument Constants Upload</span>
                            </span>
                            {(!isUploading) &&
                                <CloseIcon onClick={this.handleClose} style={{ cursor: 'pointer' }} />}
                        </Grid>
                    </DialogTitle>
                    <DialogContent className="uploadDialogContent">
                        {!isFormSubmitted &&
                            <Grid container className="uploadGrid">
                                {(this.state.step === 1 || this.state.step === 2) &&
                                    < Grid className="dragdrop">
                                        <DragDropConstants selectedPdf={this.props.selectedPdf} UpdateSelectedPdf={this.props.UpdateSelectedPdf} />
                                    </Grid>
                                }
                                {<UploadConstantsSteps handleSubmit={this.handleSubmit} handleSteps={this.handleSteps} />}
                            </Grid>
                        }
                        {isUploading &&
                            <Grid className="uploadDialogMessages">
                                <Grid container justifyContent="center">
                                    Uploading...
                                </Grid>
                                <Grid>
                                    <LinearProgressWithLabel value={progress} totalFiles={this.state.totalFiles} />
                                </Grid>
                            </Grid>
                        }
                        {isSuccess && ((responseBody.uploadedFiles.length !== 0 && responseBody.failedFiles.length === 0 && responseBody.repeatedFiles.length === 0) ?
                            <Grid>
                                <Grid container justifyContent="center">
                                    <Grid>
                                        <CheckCircleOutlineIcon className="uploadSuccess" />
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent="center">
                                    Upload Successful. Redirecting...
                                </Grid>

                            </Grid>
                            :
                            <Grid>
                                <Grid container justifyContent="center">
                                    <Grid>
                                        <WarningAmberOutlinedIcon className="uploadWarning" />
                                    </Grid>
                                </Grid>
                                {(responseBody.repeatedFiles.length > 0) &&
                                    <Grid container justifyContent="center">
                                        {`${repeated}`} cannot be uploaded because the constant file/files belong to a different product model.
                                    </Grid>
                                }
                                {(responseBody.failedFiles.length > 0) &&
                                    <Grid container justifyContent="center">
                                        {`${failed}`} cannot be uploaded. Upload Failed...
                                    </Grid>
                                }
                            </Grid>
                        )}

                        {isFailure &&
                            <Grid container justifyContent="center">
                                <Grid>
                                    <ErrorOutlineOutlinedIcon className="uploadError" />
                                </Grid>
                                <Grid container justifyContent="center">
                                    There was an error while uploading. Please try again later...
                                </Grid>
                            </Grid>
                        }
                    </DialogContent>
                </Dialog>
            </div>
        );
    }
}
const mapStateToProps = (state: any) => ({
    state: state.uploadConstants,
    displayName: state.uploadConstants.displayName,
    selectedPdf: state.uploadConstants.selectedPdf,
    selectedProductModels: state.uploadConstants.selectedProductModels,
    uploadDialogOpen: state.uploadConstants.uploadDialogOpen,
    fileType: state.uploadConstants.fileType,
    fileName: state.uploadConstants.fileName
});
const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);
export default connect(
    mapStateToProps, mapDispatchtoProps
)(UploadConstants);
